import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Edit, Eye, ChevronDown, ChevronUp, Download, Search, Briefcase } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  open: "bg-emerald-50 text-emerald-700",
  closed: "bg-gray-100 text-gray-600",
  draft: "bg-amber-50 text-amber-700",
};

const appStatusColors = {
  new: "bg-blue-50 text-blue-700",
  reviewed: "bg-amber-50 text-amber-700",
  interview: "bg-purple-50 text-purple-700",
  offered: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

export const CareersPanel = ({ getAuthHeader, onRefresh }) => {
  const [activeView, setActiveView] = useState("vacancies");
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedApp, setExpandedApp] = useState(null);
  const [form, setForm] = useState({
    title: "", department: "", location: "", location_type: "on-site",
    employment_type: "full-time", description: "", requirements: "",
    salary_range: "", deadline: "",
  });

  const fetchData = async () => {
    try {
      const auth = getAuthHeader();
      const [vRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/vacancies`, auth),
        axios.get(`${API}/admin/applications`, auth),
      ]);
      setVacancies(vRes.data);
      setApplications(aRes.data);
      setLoaded(true);
    } catch { toast.error("Failed to load careers data"); }
  };

  if (!loaded) fetchData();

  const resetForm = () => {
    setForm({ title: "", department: "", location: "", location_type: "on-site", employment_type: "full-time", description: "", requirements: "", salary_range: "", deadline: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const auth = getAuthHeader();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/vacancies/${editingId}`, form, auth);
        toast.success("Vacancy updated");
      } else {
        await axios.post(`${API}/admin/vacancies`, form, auth);
        toast.success("Vacancy published");
      }
      resetForm();
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (v) => {
    setForm({
      title: v.title || "", department: v.department || "", location: v.location || "",
      location_type: v.location_type || "on-site", employment_type: v.employment_type || "full-time",
      description: v.description || "", requirements: v.requirements || "",
      salary_range: v.salary_range || "", deadline: v.deadline || "",
    });
    setEditingId(v.id);
    setShowForm(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/vacancies/${id}`, { status: newStatus }, getAuthHeader());
      toast.success("Status updated");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleDeleteVacancy = async (id) => {
    if (!window.confirm("Delete this vacancy and all applications?")) return;
    try {
      await axios.delete(`${API}/admin/vacancies/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleAppStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/admin/applications/${appId}/status`, { status }, getAuthHeader());
      toast.success(`Application marked as ${status}`);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleDeleteApp = async (appId) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${appId}`, getAuthHeader());
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const filteredVacancies = vacancies.filter(v => `${v.title} ${v.department}`.toLowerCase().includes(search.toLowerCase()));
  const filteredApps = applications.filter(a => `${a.name} ${a.email} ${a.vacancy_title}`.toLowerCase().includes(search.toLowerCase()));

  const newApps = applications.filter(a => a.status === "new").length;

  return (
    <div className="space-y-4">
      {/* Sub-nav */}
      <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
        <button
          onClick={() => setActiveView("vacancies")}
          data-testid="careers-tab-vacancies"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeView === "vacancies" ? "bg-warm-50 text-warm-700" : "text-gray-500 hover:text-gray-700"}`}
        >
          Vacancies ({vacancies.length})
        </button>
        <button
          onClick={() => setActiveView("applications")}
          data-testid="careers-tab-applications"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeView === "applications" ? "bg-warm-50 text-warm-700" : "text-gray-500 hover:text-gray-700"}`}
        >
          Applications ({applications.length})
          {newApps > 0 && <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">{newApps}</span>}
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-10" />
        </div>
        {activeView === "vacancies" && (
          <Button data-testid="add-vacancy-btn" onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
            <Plus className="w-4 h-4" />{showForm ? "Cancel" : "New Vacancy"}
          </Button>
        )}
      </div>

      {/* Vacancy Form */}
      {activeView === "vacancies" && showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Job Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Department</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Baltimore, MD" className="mt-1" /></div>
          <div><Label className="text-xs">Location Type</Label>
            <select value={form.location_type} onChange={e => setForm({ ...form, location_type: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="on-site">On-Site</option><option value="remote">Remote</option><option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div><Label className="text-xs">Employment Type</Label>
            <select value={form.employment_type} onChange={e => setForm({ ...form, employment_type: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="full-time">Full-Time</option><option value="part-time">Part-Time</option><option value="volunteer">Volunteer</option><option value="internship">Internship</option><option value="contract">Contract</option>
            </select>
          </div>
          <div><Label className="text-xs">Salary Range</Label><Input value={form.salary_range} onChange={e => setForm({ ...form, salary_range: e.target.value })} placeholder="e.g. $40,000 - $55,000" className="mt-1" /></div>
          <div><Label className="text-xs">Application Deadline</Label><Input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" placeholder="Describe the role, responsibilities, and what a typical day looks like..." /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Requirements</Label><textarea value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} rows={4} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" placeholder="List qualifications, experience, skills needed..." /></div>
          <div><Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">{editingId ? "Update" : "Publish"} Vacancy</Button></div>
        </form>
      )}

      {/* Vacancies List */}
      {activeView === "vacancies" && (
        <div className="space-y-3">
          {filteredVacancies.map(v => (
            <div key={v.id} className="bg-white border border-gray-100 rounded-xl px-5 py-4 flex items-start justify-between hover:shadow-sm transition-shadow">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{v.title}</span>
                  <select
                    value={v.status}
                    onChange={e => handleStatusChange(v.id, e.target.value)}
                    className={`text-xs px-2 py-0.5 rounded-full font-medium border-0 cursor-pointer ${statusColors[v.status]}`}
                  >
                    <option value="open">Open</option><option value="closed">Closed</option><option value="draft">Draft</option>
                  </select>
                  <span className="text-xs text-gray-400">{v.application_count || 0} applications</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {[v.department, v.location, v.employment_type, v.deadline ? `Deadline: ${v.deadline}` : ""].filter(Boolean).join(" | ")}
                </p>
              </div>
              <div className="flex items-center gap-1 ml-3">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(v)} className="h-8 w-8 p-0"><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeleteVacancy(v.id)} className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          {filteredVacancies.length === 0 && <div className="py-8 text-center text-gray-400 text-sm">No vacancies yet</div>}
        </div>
      )}

      {/* Applications List */}
      {activeView === "applications" && (
        <div className="space-y-3">
          {filteredApps.map(a => (
            <div key={a.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden">
              <div
                className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50/50"
                onClick={() => setExpandedApp(expandedApp === a.id ? null : a.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{a.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${appStatusColors[a.status]}`}>{a.status}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {a.vacancy_title} | {a.email} | {new Date(a.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  {expandedApp === a.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {expandedApp === a.id && (
                <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Email:</span> <span className="text-gray-900">{a.email}</span></div>
                    <div><span className="text-gray-500">Phone:</span> <span className="text-gray-900">{a.phone || "N/A"}</span></div>
                    <div><span className="text-gray-500">Position:</span> <span className="text-gray-900">{a.vacancy_title}</span></div>
                    <div><span className="text-gray-500">Applied:</span> <span className="text-gray-900">{new Date(a.created_at).toLocaleString()}</span></div>
                  </div>
                  {a.cover_letter && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Cover Letter</p>
                      <p className="text-sm text-gray-700 whitespace-pre-line bg-white p-3 rounded-lg">{a.cover_letter}</p>
                    </div>
                  )}
                  {a.resume_file && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/resumes/${a.resume_file}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-warm-700 hover:text-warm-800 font-medium"
                      data-testid={`download-resume-${a.id}`}
                    >
                      <Download className="w-4 h-4" />Download Resume
                    </a>
                  )}
                  <div className="flex items-center gap-2 flex-wrap pt-2">
                    <span className="text-xs text-gray-500 mr-1">Set status:</span>
                    {["reviewed", "interview", "offered", "rejected"].map(s => (
                      <button
                        key={s}
                        onClick={() => handleAppStatus(a.id, s)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all ${a.status === s ? appStatusColors[s] : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                    <button
                      onClick={() => handleDeleteApp(a.id)}
                      className="text-xs text-red-500 hover:text-red-700 ml-2"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filteredApps.length === 0 && <div className="py-8 text-center text-gray-400 text-sm">No applications received</div>}
        </div>
      )}
    </div>
  );
};
